import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, SecurityContext } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Store } from '@ngrx/store';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import {
  AddOrderMeta,
  SocialLoginCancelled,
  SocialLoginFailed,
  SocialLoginLoading,
  SocialLoginSuccess,
  SocialLogout,
} from 'src/app/public/store/+state/stores.actions';
import {
  getSelectedStore,
  getSocialAuth,
  getTokens,
} from 'src/app/public/store/+state/stores.selectors';
import { environment } from 'src/environments/environment';
import { ClientStore } from '../stores/stores';
import { LocationService } from './location.service';
import { StoreService } from './store/store.service';
import {
  CustomerSocialLoginResponse,
  SocialAccountLoginDetails,
  Tokens,
} from './store/types/CustomerSocialLogin';
import {
  combineLatest,
  Observable,
  of,
  Subject,
  takeUntil,
  throwError,
} from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { LocalStorageService } from '../local-storage.service';
import {
  asUncheckedHandoff,
  AuthHandoffPayload,
  clearLandingAuthHandoff,
  clearUncheckedHandoff,
  CUSTOMER_AUTH_DATA_PARAM,
  CUSTOMER_AUTH_STORAGE_KEY,
  encodeAuthDataParam,
  isJwtOfSessionCustomer,
  handoffToStore,
  isPersistableAuthData,
  isUncheckedHandoff,
  parseAuthDataParam,
  peekLandingAuthHandoff,
  readAuthDataFromHashString,
  shouldAcceptAuthHandoff,
  stripAuthDataFromAddressBar,
  takeRejectedAuthHandoff,
} from './customer-auth-url-handoff';

/** Store setting that decides whether a store offers customer login at all. */
export const CUSTOMER_AUTHENTICATION_SELECTION =
  'CUSTOMER_AUTHENTICATION_SELECTION';

/** The values {@link CUSTOMER_AUTHENTICATION_SELECTION} can take. */
export type CustomerAuthenticationSelection =
  | 'DISABLED'
  | 'OPTIONAL'
  | 'MANDATORY';

/**
 * The slice of a store's settings customer-auth visibility reads. `ClientStore`
 * carries an index signature, so a whole `settings` object is assignable here,
 * but a caller passing this type gets the key spelled for it and the value
 * union checked instead of two bare string literals.
 */
export interface CustomerAuthStoreSettings {
  [CUSTOMER_AUTHENTICATION_SELECTION]?:
    | CustomerAuthenticationSelection
    | string;
  MEMBER_ONLY_AUTHENTICATION?: boolean;
  [key: string]: unknown;
}

/**
 * What the caller of a sign-in that can be abandoned hears back. Exactly one
 * of the three runs per attempt.
 *
 * <p>`onCancel` is the third outcome card #11708 adds. Card #11698 ends an
 * attempt that is no longer the current one with `takeUntil`, and `takeUntil`
 * *completes* the request rather than erroring it, so neither `onSuccess` nor
 * `onError` runs. Without a third callback the caller is never told the
 * attempt ended, and a loading flag it set before the call is never cleared —
 * which left the checkout sign-in panel under its own overlay until the page
 * was reloaded. Nothing went wrong in that case, so a cancel is silent: no
 * error, no toast.
 */
export interface CancellableSignInCallbacks {
  onSuccess?: (response: CustomerSocialLoginResponse) => void;
  /** `unknown` rather than `HttpErrorResponse`: an observable can error with
   * anything, and the one consumer narrows it before reading it. */
  onError?: (error: unknown) => void;
  onCancel?: () => void;
}

/**
 * Whether the backend refused a refresh token, as opposed to not answering.
 * `POST /api/v1/auth/token/refresh` answers 401 for a token it no longer holds
 * (revoked by a logout, or expired) and for a disabled account.
 */
function isRefreshTokenRefused(error: unknown): boolean {
  return error instanceof HttpErrorResponse && error.status === 401;
}
import { LogService } from '../shared/logging/LogService';

/** One membership question: is this customer a member of this store? */
interface MembershipCheck {
  storeId: number;
  userId: number;
}

/** The same known customer on the same store. */
function isSameMembership(
  a: MembershipCheck | null,
  b: MembershipCheck,
): boolean {
  return a?.userId != null && a.userId === b.userId && a.storeId === b.storeId;
}

/**
 * Where the last "yes" of the membership check is kept, so that a page reload
 * still knows it (card #11696). It holds one {@link MembershipCheck}.
 */
export const CUSTOMER_MEMBERSHIP_STORAGE_KEY = '__customer_membership__';

@Injectable({
  providedIn: 'root',
})
export class CustomerAuthService {
  readonly tokenCookieName = CUSTOMER_AUTH_STORAGE_KEY;
  readonly authDataParam = CUSTOMER_AUTH_DATA_PARAM;
  selectedStore: ClientStore;
  isAdminOrderCaptureUpdate: boolean;
  private sociallyLoggedIn = false;
  private isMember = false;
  /**
   * The store and customer of the last "yes" from the membership check. Set
   * through {@link setConfirmedMembership}, which keeps it for a page reload.
   */
  private confirmedMembership: MembershipCheck | null = null;
  /**
   * The membership check whose answer still counts: the latest one, and none
   * while the selected store is not members-only.
   */
  private latestMembershipCheck: MembershipCheck | null = null;
  private readonly REFRESH_TOKEN_INTERVAL = 4 * 60 * 1000; // 4 minutes
  private refreshTokenInterval: any;
  /**
   * Emits when the session that was current when a request started is no
   * longer current: raised at the start of `logout()` and of each sign-in
   * (`login()`, and `passwordLogin()` and `customerVerification()` through
   * {@link takeOverSession}). Every call that writes the session ends with
   * `takeUntil(this.sessionChanged$)`, so its late answer or late error is
   * dropped instead of bringing back, or ending, a session that is no longer
   * current (card #11698). A sign-in also stops the previous session's 4-minute
   * timer, so no later renewal of that session can start; only a refused email
   * or code sign-in hands it back (see {@link onSignInRefused}).
   * `signOut()`'s revoke is the one exception: it has no
   * `takeUntil`, because unsubscribing would abort the revoke itself, so it
   * checks the signal instead.
   *
   * `takeUntil` *completes* the request rather than erroring it, so an
   * abandoned attempt is a third outcome, neither an answer nor an error. The
   * calls whose outcome a screen is waiting on report it: the two sign-ins by
   * email through `onCancel`, the social one through `SocialLoginCancelled`
   * (card #11708). See {@link CancellableSignInCallbacks} and
   * {@link reportCancel}.
   *
   * Never call `complete()` on it: a completed notifier never emits again, so
   * the guard would silently stop working for the rest of the page.
   */
  private readonly sessionChanged$ = new Subject<void>();
  private readonly membershipConfirmedSubject = new Subject<void>();
  /**
   * Card #11696: emits when the answer to the latest membership check turns
   * the signed-in customer into a member: a sign-in, a restore with no kept
   * "yes", or a move into another members-only store. That answer only sets
   * the private `isMember` and dispatches nothing, so a page that decides on
   * the social login slice never heard it. The login page stayed empty, and
   * the header kept Login, until the next 4-minute renewal. A member's own
   * renewal emits nothing, because it keeps them a member.
   * `isUserLoggedIn()` stays the answer; this only says "read it again".
   */
  readonly membershipConfirmed$ =
    this.membershipConfirmedSubject.asObservable();
  /** The store the restored session was last checked for (#10368); see `checkCurrentSession`. */
  private sessionCheckedForStoreId: number | undefined;

  constructor(
    private store: Store,
    public sanitizer: DomSanitizer,
    private locationService: LocationService,
    private storeService: StoreService,
    private toasterService: ToastrService,
    private translateService: TranslateService,
    private localStorageService: LocalStorageService,
    private logger: LogService,
  ) {
    // No handoff work here on purpose. `main.ts` owns capture → persist →
    // strip and runs it before `bootstrapModule`, so by the time anything can
    // inject this service the session is already in storage and the bar is
    // clean. Repeating it in the constructor made the operation depend on
    // which component, guard or interceptor happened to be built first — an
    // order that moves with routing and lazy-loading — and left the same
    // sequence with three owners to keep in step. What remains is the lazy
    // consumer, `parseAndSetAuthFromUrl()`, called by `StoreLoadingComponent`
    // on store LOADED, which also covers search-string links already in the
    // wild and the snapshot a pre-bootstrap redirect rewrote away.
    this.onInit();
  }

  private onInit(): void {
    this.isAdminOrderCaptureUpdate =
      this.locationService.isAdminOrderCaptureUpdate();
    this.listenToLoginEvents();

    combineLatest([
      this.store.select(getSelectedStore),
      this.store.select(getSocialAuth),
    ])
      .pipe(filter(([selectedStore]) => !!selectedStore))
      .subscribe(([selectedStore, socialAuth]) => {
        this.selectedStore = selectedStore;
        if (!this.membershipRequired()) {
          // Card #11696: this store asks no membership question, so an answer
          // still on its way for the previous store must not sign out here.
          // It also runs for the empty store LoadStore puts in place while a
          // store loads; the loaded store then asks again.
          this.latestMembershipCheck = null;
        }
        if (socialAuth?.socialLoginState && this.sociallyLoggedIn) {
          const socialLoginResponse = socialAuth.socialLoginState;
          const { socialAuthStatus } = socialLoginResponse;

          if (socialAuthStatus === 'SUCCESS') {
            if (this.membershipRequired()) {
              this.checkMembership({
                storeId: selectedStore.id,
                userId: socialLoginResponse.userId,
              });
            }

            const event = {
              src: 'CustomerAuthService',
              description: 'Social login successfull',
            };
            if (socialLoginResponse.userId) {
              this.store.dispatch(
                new AddOrderMeta(
                  'customerUserId',
                  socialLoginResponse.userId,
                  event,
                ),
              );
            }
          }

          if (socialAuthStatus === 'FAILED') {
            this.logout();
          }
        }
      });
  }

  /**
   * Asks the backend whether the signed-in customer is a member of the store.
   *
   * Card #11696: every session refresh (every 4 minutes) and every update
   * of the selected store asks again for the same customer and store. A member
   * stays a member while that check runs. Before, they counted as signed out
   * until the answer, and My Account and the cart, which listen to the same
   * sign-in state, sent them to the login page in that moment.
   *
   * Another customer or another store starts as not a member until the
   * answer, and a "yes" then tells the pages through
   * {@link membershipConfirmed$}. Only the answer to the latest check counts,
   * and none counts once the customer is on a store that is not members-only.
   * A "no" or a failed check still signs the customer out. The first check
   * after a page reload keeps a member too, see
   * {@link restoreConfirmedMembership}.
   */
  private checkMembership(check: MembershipCheck): void {
    if (!isSameMembership(this.confirmedMembership, check)) {
      this.isMember = false;
      this.setConfirmedMembership(null);
    }
    this.latestMembershipCheck = check;
    this.storeService
      .customerCheckMembership(check.storeId)
      .pipe(takeUntil(this.sessionChanged$))
      .subscribe(
        (res) => {
          if (this.latestMembershipCheck !== check) {
            return;
          }
          const becameMember = res.isMember && !this.isMember;
          this.isMember = res.isMember;
          this.setConfirmedMembership(res.isMember ? check : null);
          if (!res.isMember) {
            this.onInvalidMember();
          } else if (becameMember) {
            // After the flags, so a listener reads the customer as signed in.
            this.membershipConfirmedSubject.next();
          }
        },
        () => {
          if (this.latestMembershipCheck === check) {
            this.onInvalidMember();
          }
        },
      );
  }

  /** Sets the last "yes", and keeps it in storage for a page reload. */
  private setConfirmedMembership(check: MembershipCheck | null): void {
    this.confirmedMembership = check;
    if (check) {
      this.localStorageService.writeSavedState(
        CUSTOMER_MEMBERSHIP_STORAGE_KEY,
        check,
      );
    } else {
      this.localStorageService.removeSavedState(
        CUSTOMER_MEMBERSHIP_STORAGE_KEY,
      );
    }
  }

  /**
   * Card #11696: a page reload restores the saved session, and the membership
   * check after it had no "yes" in memory. So a member counted as signed out
   * until the answer, and My Account and the cart sent them to the login page
   * in that moment, as the 4-minute renewal did before. The last "yes" is kept
   * in storage, so the same customer on the same store stays a member while
   * that first check runs. A "no" or a failed check still signs them out.
   *
   * A session from a link that is not checked yet gets no head start: its
   * token may belong to another account (story #11691).
   *
   * A "yes" for another store or another customer is dropped first. A store
   * change while the reload's refresh still runs restores the session again,
   * and the "yes" for the first store must not count on the second.
   */
  private restoreConfirmedMembership(
    data: CustomerSocialLoginResponse,
    storeId: number | undefined,
  ): void {
    if (storeId == null || !this.membershipRequired()) {
      return;
    }
    const restored: MembershipCheck = { storeId, userId: data.userId };
    if (!isSameMembership(this.confirmedMembership, restored)) {
      this.isMember = false;
      this.confirmedMembership = null;
    }
    if (isUncheckedHandoff(data)) {
      return;
    }
    const remembered = this.localStorageService.readSavedState(
      CUSTOMER_MEMBERSHIP_STORAGE_KEY,
    ) as MembershipCheck | null;
    if (isSameMembership(remembered, restored)) {
      this.isMember = true;
      this.confirmedMembership = restored;
    }
  }

  private listenToLoginEvents() {
    window.addEventListener(
      'message',
      (event) => {
        const { data } = event;
        this.login(data);
      },
      false,
    );
  }

  enableCustomerAuth(): boolean {
    return this.enableCustomerAuthForSettings(this.selectedStore?.settings);
  }

  /**
   * Whether a store (parent or a specific child row) offers customer login.
   * Session itself stays global; only the setting is per-store (#11246 / Anant).
   */
  enableCustomerAuthForSettings(
    settings: CustomerAuthStoreSettings | null | undefined,
  ): boolean {
    const selection = settings?.[CUSTOMER_AUTHENTICATION_SELECTION];
    return (
      (selection === 'MANDATORY' || selection === 'OPTIONAL') &&
      !this.locationService.isAdminOrderCaptureUpdate()
    );
  }

  customerAuthRequired(): boolean {
    return (
      this.selectedStore?.settings?.[CUSTOMER_AUTHENTICATION_SELECTION] ===
        'MANDATORY' && !this.locationService.isAdminOrderCaptureUpdate()
    );
  }

  membershipRequired(): boolean {
    return (
      this.enableCustomerAuth() &&
      this.selectedStore?.settings['MEMBER_ONLY_AUTHENTICATION']
    );
  }

  isUserLoggedIn(): boolean {
    return (
      this.sociallyLoggedIn && (!this.membershipRequired() || this.isMember)
    );
  }

  /**
   * Restores the saved session on a store load, and checks it again whenever the store changes.
   *
   * <p>The session is saved under one key for every store, and in the apps all stores share one
   * WebView, so switching store keeps it. Since #10368 an account can belong to one store's group,
   * so the refresh says which store it is for and the backend refuses another group's account;
   * the error path then logs the customer out instead of carrying store X's account into store Y.
   */
  checkCurrentSession() {
    try {
      // readSavedState is the reader that matches saveState's writer; the old
      // getSavedState + JSON.parse pair decoded the same two levels by hand.
      const data = this.localStorageService.readSavedState(
        this.tokenCookieName,
      ) as CustomerSocialLoginResponse | null;
      const storeId = this.selectedStore?.id;
      if (
        data &&
        (!this.sociallyLoggedIn || storeId !== this.sessionCheckedForStoreId)
      ) {
        // Card #11561: `login()` saves the provider payload before sign-in
        // finishes, so a payload without tokens is no session to restore.
        if (!data.tokens?.refreshToken) {
          this.localStorageService.removeSavedState(this.tokenCookieName);
          return;
        }

        const event = {
          src: 'CustomerAuthService',
          description: 'Current session loaded',
        };

        this.sociallyLoggedIn = true;
        this.sessionCheckedForStoreId = storeId;
        this.restoreConfirmedMembership(data, storeId);

        this.refreshSessionTokens(data.tokens.refreshToken, storeId).subscribe({
          next: (res) => {
            // Story #11691: a handoff link can name this customer and carry
            // another account's refresh token. Then the backend has just
            // issued a JWT for that other account, and keeping the session
            // would show this customer's name while orders and points go to
            // the other account. End it, as the backend had refused it.
            // Only a session installed from a link and not checked yet is
            // checked, and only once. A session signed in on this site keeps
            // its old email when a store admin changes it, and is still the
            // customer's own (see isJwtOfSessionCustomer).
            if (isUncheckedHandoff(data)) {
              if (!isJwtOfSessionCustomer(data, res?.jwt)) {
                console.warn(
                  'Ending customer session: its token belongs to another account.',
                );
                this.endStoredSession(true);
                return;
              }
              clearUncheckedHandoff(data);
            }
            // The stored session is alive, so a handoff turned away for it
            // stays turned away (story #11691: A stays signed in).
            takeRejectedAuthHandoff();
            data.tokens = res;
            this.saveState(data);
            this.store.dispatch(new SocialLoginSuccess(data, event));
            this.store.dispatch(
              new AddOrderMeta('customerEmail', data.email, event),
            );
            this.startRefreshTokenInterval(data);
          },
          error: (error) => this.endStoredSession(isRefreshTokenRefused(error)),
        });
      }
    } catch (error) {
      this.localStorageService.removeSavedState(this.tokenCookieName);
      console.error('An error occurred:', error);
    }
  }

  /**
   * The stored session could not be refreshed, so it ends here, as it always
   * has. Story #11691: if the backend refused it (`refused`), and this page
   * turned away a handoff in favour of that session, the handoff is installed
   * and checked instead. That is the same as a first arrival with no session.
   * The backend refuses a refresh token that a logout on another site of the
   * chain revoked, and a handed-over session whose token turns out to be
   * another account's counts as refused too. A network error, timeout or
   * server error says nothing about the session, so the handoff is dropped
   * then. It is taken either way, so it is tried once at most: if its refresh
   * fails too, the customer is signed out. It came from a link, so it is
   * stored marked for the same check of whose token it is.
   */
  private endStoredSession(refused: boolean): void {
    const rejectedHandoff = takeRejectedAuthHandoff();
    this.logout();
    if (
      refused &&
      rejectedHandoff &&
      this.saveState(asUncheckedHandoff(rejectedHandoff))
    ) {
      this.checkCurrentSession();
    }
  }

  /**
   * The store to sign in on (#10368). A customer may have an account that belongs to this store
   * rather than a global one, and the backend can only find it if the request says which store.
   *
   * <p>`selectedStore` is set from the `getSelectedStore` selector, filtered on truthy, so on a
   * storefront page it is populated before any of these flows can be reached — `login()` already
   * dereferences `this.selectedStore.address.country.code` unguarded and would have thrown long
   * ago otherwise. It is deliberately not a hard gate: a missing store is legitimate outside a
   * storefront context (admin order capture), where the global account is the right answer.
   *
   * <p>What is new is the cost of being wrong: without a store id the backend resolves the global
   * account, so a customer who only has a store account is told "invalid credentials" on their own
   * store's login screen. That has to be diagnosable after the fact, and a `console.warn` is not:
   * it lives only in the browser of the customer who hit it, and inside the Capacitor WebView
   * nobody can ever read it. It goes through `LogService` instead, which publishes to the backend
   * log pipeline. The level is `error` because that is the only one above the production
   * threshold (`environment.logLevel` is `ERROR` in prod) — `info` would be dropped and the entry
   * would be no more collectable than the `console.warn` it replaces.
   *
   * <p>Order capture is excluded rather than logged: there the admin is taking the order and the
   * global account genuinely is the right answer, so it is the expected case, not a degradation.
   * Logging it would bury the real one. Read live from the router, like `enableCustomerAuth` and
   * `customerAuthRequired` do, since this singleton outlives any one navigation.
   */
  private storeIdForAuth(flow: string): number | undefined {
    if (
      !this.selectedStore?.id &&
      !this.locationService.isAdminOrderCaptureUpdate()
    ) {
      this.logger.error(
        `[CustomerAuthService] no selected store for ${flow}; the request will resolve the global account only`,
      );
    }
    return this.selectedStore?.id;
  }

  login(data: any) {
    if (data?.mode === 'customer-login') {
      // Card #11708: not `takeOverSession()`. The flag of a social sign-in
      // still waiting stays up, because this one raises it again on the next
      // line. Lowered in between, the abandoned attempt's INITIAL would tell
      // every screen that nobody is signed in, for an instant, and a screen
      // that requires sign-in sends the customer to the login page on that.
      this.markSessionChanged();
      this.clearRefreshTokenInterval();
      this.sociallyLoggedIn = true;
      this.saveState(data);
      const socialAccountDetails: SocialAccountLoginDetails = {
        provider: (data.provider as string).toLowerCase(),
        accessToken: data.authToken || data.accessToken,
        countryId: this.selectedStore?.address?.country?.code,
        appleCode: data.appleCode,
        firstName: data.firstName,
        lastName: data.lastName,
        storeId: this.storeIdForAuth('social login'),
      };

      const event = {
        src: 'CustomerAuthService',
        description: 'User Social Authentication',
      };
      this.store.dispatch(new AddOrderMeta('customerEmail', data.email, event));
      this.store.dispatch(new SocialLoginLoading(event));

      let answered = false;

      this.storeService
        .socialLogin(socialAccountDetails)
        .pipe(takeUntil(this.sessionChanged$))
        .subscribe({
          next: (res) => {
            answered = true;
            this.saveState(res);
            this.store.dispatch(new SocialLoginSuccess(res, event));
            this.startRefreshTokenInterval(res);
          },
          error: (error) => {
            answered = true;
            // Card #11561: the storefront's own action, so the slice leaves
            // LOADING and the FAILED branch in `onInit` ends the session.
            this.store.dispatch(new SocialLoginFailed(event));
          },
          // Card #11708: the attempt was abandoned, and the slice is still on
          // LOADING from the dispatch above, which keeps checkout's freeze
          // layer over the whole page. Only the status is reset, and no
          // session state is written — see `SocialLoginCancelled`.
          complete: () =>
            this.reportCancel(answered, () =>
              this.store.dispatch(new SocialLoginCancelled(event)),
            ),
        });
    }
  }

  doRefreshToken(data: CustomerSocialLoginResponse) {
    const event = {
      src: 'CustomerAuthService',
      description: 'Refreshing user token',
    };

    this.refreshSessionTokens(
      data.tokens.refreshToken,
      this.selectedStore?.id,
    ).subscribe({
      next: (res) => {
        data.tokens = res;
        this.saveState(data);
        this.store.dispatch(new SocialLoginSuccess(data, event));
      },
      error: () => this.logout(),
    });
  }

  /** The token refresh of the current session; see `sessionChanged$`. */
  private refreshSessionTokens(refreshToken: string, storeId?: number) {
    return this.storeService
      .refreshToken(refreshToken, storeId)
      .pipe(takeUntil(this.sessionChanged$));
  }

  /**
   * The session is changing (a logout or a new sign-in), so every late answer
   * for the old one is dropped; see `sessionChanged$`. A handoff this page
   * turned away (#11691) is dropped too. It waits for the old session's check,
   * and that check's answer is now dropped, so it would otherwise stay and be
   * installed later in place of whoever signed in next. `endStoredSession`
   * takes it before it logs out, so that path still installs it.
   */
  private markSessionChanged(): void {
    takeRejectedAuthHandoff();
    this.sessionChanged$.next();
  }

  /**
   * The first step of an email or code sign-in: it takes the session over
   * from whatever was current, so a late answer for that can no longer land
   * (card #11698). `login()` does the same, but keeps the flag (see there).
   *
   * Card #11708: a social sign-in raises `sociallyLoggedIn` before its request
   * goes out, with only the provider's payload saved. If one is still waiting,
   * raising the signal abandons it, and its `SocialLoginCancelled` puts the
   * status back to INITIAL at once. Its flag goes first, so that every screen
   * reading that status sees "nobody is signed in", which is the truth until
   * this sign-in answers. A screen that reads the status alone is not told
   * again: if this sign-in is then refused, the status does not change. A
   * live session has its refresh token saved and keeps its flag: it is still
   * the session until this sign-in answers.
   */
  private takeOverSession(): void {
    if (!this.savedLiveSession()) {
      this.sociallyLoggedIn = false;
    }
    this.markSessionChanged();
    this.clearRefreshTokenInterval();
  }

  /** The session saved in this browser, if it is a real one (it has tokens). */
  private savedLiveSession(): CustomerSocialLoginResponse | null {
    const saved = this.localStorageService.readSavedState(
      this.tokenCookieName,
    ) as CustomerSocialLoginResponse | null;
    return isPersistableAuthData(saved) ? saved : null;
  }

  /**
   * An email or code sign-in was refused (card #11708). Both methods end the
   * same way. With no real session saved, nobody is signed in. A live session
   * (a wrong code under "Forgot password?" while signed in) is still the
   * session: it keeps its flag, so its Log out still revokes it on the backend
   * (card #11698), and it gets back the renewal that `takeOverSession` stopped
   * when the attempt started, or its token would lapse a few minutes later and
   * sign the customer out.
   */
  private onSignInRefused(): void {
    const live = this.savedLiveSession();
    if (!live) {
      this.sociallyLoggedIn = false;
    } else if (this.sociallyLoggedIn) {
      this.startRefreshTokenInterval(live);
    }
    if (!this.sociallyLoggedIn) {
      // Card #11696: nobody is signed in, so the next sign-in starts as not a
      // member. A restored session may have set it from the kept "yes". A
      // live session keeps its membership, as it keeps its flag.
      this.isMember = false;
      this.setConfirmedMembership(null);
    }
  }

  private startRefreshTokenInterval(data: CustomerSocialLoginResponse) {
    this.clearRefreshTokenInterval();
    this.refreshTokenInterval = setInterval(() => {
      this.doRefreshToken(data);
    }, this.REFRESH_TOKEN_INTERVAL);
  }

  private clearRefreshTokenInterval() {
    if (this.refreshTokenInterval) {
      clearInterval(this.refreshTokenInterval);
      this.refreshTokenInterval = null;
    }
  }

  saveState(data: unknown): boolean {
    return this.localStorageService.writeSavedState(this.tokenCookieName, data);
  }

  passwordLogin(
    { email, password }: { email: string; password: string },
    { onSuccess, onError, onCancel }: CancellableSignInCallbacks = {},
  ) {
    this.takeOverSession();
    const socialAccountDetails: SocialAccountLoginDetails = {
      username: email,
      password: password,
      storeId: this.storeIdForAuth('password login'),
    };

    const event = {
      src: 'CustomerAuthService',
      description: 'User authenticated with password',
    };

    let answered = false;

    this.storeService
      .socialLogin(socialAccountDetails)
      .pipe(takeUntil(this.sessionChanged$))
      .subscribe({
        next: (response) => {
          answered = true;
          this.sociallyLoggedIn = true;
          this.saveState(response);
          this.store.dispatch(new SocialLoginSuccess(response, event));
          this.store.dispatch(new AddOrderMeta('customerEmail', email, event));
          // Story #11691: as for social sign-in, so the JWT stays valid for the
          // logout revoke and a revoke made elsewhere ends this session.
          this.startRefreshTokenInterval(response);
          onSuccess && onSuccess(response);
        },
        error: (error) => {
          answered = true;
          this.onSignInRefused();
          onError && onError(error);
        },
        complete: () => this.reportCancel(answered, onCancel),
      });
  }

  /**
   * Reports that an attempt was abandoned, from the `complete` of a request
   * piped through `takeUntil(this.sessionChanged$)` (card #11708). `complete`
   * also fires on the ordinary successful path, straight after `next`, so the
   * cancel is the completion that arrived without an answer — which is what
   * `answered` distinguishes. Every cancellable call routes through here so
   * that test cannot be forgotten by the next one added.
   *
   * <p>`report` writes no session state, in any caller. From the moment
   * `sessionChanged$` is raised the call that raised it owns the session, so a
   * superseded attempt must not touch `sociallyLoggedIn` or the saved state,
   * which is exactly what card #11698 forbids. It tells whoever was waiting,
   * and nothing else.
   */
  private reportCancel(answered: boolean, report?: () => void): void {
    if (!answered) {
      report && report();
    }
  }

  customerRegistrationV2(
    email: string,
    captchaToken: string,
    {
      onSuccess,
      onError,
    }: {
      onSuccess?: (response: any) => void;
      onError?: (error: any) => void;
    } = {},
  ) {
    this.storeService
      .customerRegistrationV2(
        email,
        captchaToken,
        this.storeIdForAuth('sign-up'),
      )
      .subscribe(
        (res) => onSuccess && onSuccess(res),
        (error) => {
          onError && onError(error);
        },
      );
  }

  customerResetByCode(
    { email }: { email: string },
    {
      onSuccess,
      onError,
    }: {
      onSuccess?: (response: any) => void;
      onError?: (error: any) => void;
    } = {},
  ) {
    this.storeService
      .customerResetByCode(email, this.storeIdForAuth('reset by code'))
      .subscribe(
        (res) => onSuccess && onSuccess(res),
        (error) => onError && onError(error),
      );
  }

  customerVerification(
    { email, code }: { email: string; code: string },
    { onSuccess, onError, onCancel }: CancellableSignInCallbacks = {},
  ) {
    this.takeOverSession();

    let answered = false;

    this.storeService
      .customerActivation(email, code, this.storeIdForAuth('activation'))
      .pipe(takeUntil(this.sessionChanged$))
      .subscribe({
        next: (res) => {
          answered = true;
          this.sociallyLoggedIn = true;
          this.saveState(res);
          const event = {
            src: 'CustomerAuthService',
            description: 'User registered and verified',
          };
          this.store.dispatch(new SocialLoginSuccess(res, event));
          this.store.dispatch(new AddOrderMeta('customerEmail', email, event));
          // Story #11691: see passwordLogin().
          this.startRefreshTokenInterval(res);
          onSuccess && onSuccess(res);
        },
        error: (error) => {
          answered = true;
          this.onSignInRefused();
          onError && onError(error);
        },
        complete: () => this.reportCancel(answered, onCancel),
      });
  }

  changePassword(
    { code, password }: { code: string; password: string },
    {
      onSuccess,
      onError,
    }: {
      onSuccess?: (response: any) => void;
      onError?: (error: any) => void;
    } = {},
  ) {
    this.storeService.customerPasswordUpdateByCode(code, password).subscribe(
      (res) => onSuccess && onSuccess(res),
      (error) => onError && onError(error),
    );
  }

  /**
   * The customer's own "Log out" (story #11691): revoke the session on the
   * backend first, then tear down locally either way, unless the session
   * already changed meanwhile (card #11698, below). `POST /api/v1/auth/logout`
   * deletes every refresh token of the account, so a session handed to another
   * site of the same chain (or another device) cannot be refreshed any more.
   * The revoke must go out before `logout()` clears the JWT the customer
   * interceptor attaches.
   *
   * The backend revokes the account whose JWT the request carries. The request
   * is marked `CUSTOMER_LOGOUT`, so `TokenInterceptor` never puts the
   * backoffice JWT on it and it carries the customer's, also in admin order
   * capture where a backoffice session shares the browser: the customer is
   * signed out everywhere and the admin's session is left alone.
   *
   * Every sign-in path (social, password, emailed code, a restored or
   * handed-over session through `checkCurrentSession()`) sets
   * `sociallyLoggedIn` and starts the refresh timer, so none of them skips the
   * revoke or sends it with a JWT the timer let expire. A JWT can still expire
   * while the timer is paused (an app in the background, a laptop asleep), so
   * a 401 gets one retry with a fresh JWT. A revoke that still fails (no
   * network, a session already revoked elsewhere) ends the session here.
   */
  signOut(): void {
    if (!this.sociallyLoggedIn) {
      this.logout();
      return;
    }
    // The customer chose to leave, so a handoff turned away for a session
    // that is still being restored must not take its place if the backend
    // refuses that session meanwhile (`endStoredSession`).
    takeRejectedAuthHandoff();
    // Card #11698. The revoke has no `takeUntil(this.sessionChanged$)`:
    // unsubscribing would abort the request, and the backend must still sign
    // the account out everywhere. But if the session changes while it is out
    // (a logout, or the start of any sign-in), its answer ends nothing here:
    // whoever changed the session owns it now. The signal fires before any
    // teardown, so the tokens read then are still the signed-out session's,
    // and a 401 retry uses those, never a newer one's.
    let sessionChanged = false;
    let endedSessionTokens: Tokens | undefined;
    const watch = this.sessionChanged$.pipe(take(1)).subscribe(() => {
      sessionChanged = true;
      this.store
        .select(getTokens)
        .pipe(take(1))
        .subscribe((tokens) => (endedSessionTokens = tokens));
    });
    const endIfStillCurrent = () => {
      watch.unsubscribe();
      if (!sessionChanged) {
        this.logout();
      }
    };
    this.storeService
      .customerLogout()
      .pipe(
        catchError((error) =>
          this.retryLogoutWithFreshJwt(
            error,
            sessionChanged
              ? of(endedSessionTokens)
              : this.store.select(getTokens),
          ),
        ),
      )
      .subscribe({ next: endIfStillCurrent, error: endIfStillCurrent });
  }

  /**
   * The backend answers 401 to a revoke whose JWT has expired, and revokes
   * nothing. The refresh token outlives the JWT by far, so trade it for a fresh
   * JWT and send the revoke once more with it. The fresh JWT goes on that one
   * request only: putting it in the store would start the signed-in reactions
   * (membership check) of a customer who is leaving. `tokens$` gives the
   * tokens of the session signed out of; see `signOut`.
   */
  private retryLogoutWithFreshJwt(
    error: unknown,
    tokens$: Observable<Tokens | undefined>,
  ): Observable<unknown> {
    if (!(error instanceof HttpErrorResponse && error.status === 401)) {
      return throwError(() => error);
    }
    return tokens$.pipe(
      take(1),
      switchMap((tokens) =>
        tokens?.refreshToken
          ? this.storeService.refreshToken(
              tokens.refreshToken,
              this.selectedStore?.id,
            )
          : throwError(() => error),
      ),
      switchMap((fresh) => this.storeService.customerLogout(fresh.jwt)),
    );
  }

  /**
   * Tear down the customer session in this browser only, without contacting
   * the backend. Used where the session already ended (a refused or expired
   * token, a deleted profile); the customer's own "Log out" uses
   * {@link signOut}.
   */
  logout() {
    // Card #11708: lowered before the signal, because the signal abandons a
    // social sign-in that is still waiting, and its `SocialLoginCancelled`
    // reaches every screen at once. Read with the flags still up, the status
    // it returns to says "signed in" to a screen that combines the two, and
    // the `SocialLogout` below does not change the status, so such a screen
    // would not be told again. No request can answer between these lines.
    this.sociallyLoggedIn = false;
    this.isMember = false;
    this.setConfirmedMembership(null);
    this.latestMembershipCheck = null;
    this.markSessionChanged();
    this.clearRefreshTokenInterval();
    this.localStorageService.removeSavedState(this.tokenCookieName);
    const event = {
      src: 'CustomerAuthService',
      description: 'User logged out',
    };
    this.store.dispatch(new AddOrderMeta('customerUserId', null, event));
    this.store.dispatch(new SocialLogout(event));
    const retries = 3;
    this.postMessageToIframe(retries);
  }

  /**
   * Ends the customer session the backend refused, but only while
   * `rejectedJwt` is still the session's token: a refusal of a token the
   * session has already replaced, by a refresh or a new sign-in, ends nothing
   * (card #11561). Called by `CustomerTokenInterceptor`.
   *
   * On a bottom menu bar page the customer stays where they are and the page
   * offers its own login, as it does for a customer who never signed in; a
   * store that requires sign-in is still sent to the login by
   * `StoreLoadingComponent` (story #10267). Every other page reopens the login
   * (card #11440).
   */
  endRejectedSession(rejectedJwt: string): void {
    this.store
      .select(getTokens)
      .pipe(take(1))
      .subscribe((tokens) => {
        if (tokens?.jwt === rejectedJwt) {
          this.logout();
          if (!this.locationService.isBottomBarPageView()) {
            this.locationService.navigateToLogin();
          }
        }
      });
  }

  private postMessageToIframe(retries = 0) {
    const iframe = window.frames['user-login']?.contentWindow;
    const userLogin = this.sanitizer.bypassSecurityTrustResourceUrl(
      environment.adminHostURL + '/user-login',
    );
    if (iframe) {
      iframe.postMessage(
        { logout: true },
        this.sanitizer.sanitize(SecurityContext.URL, userLogin),
      );
    } else if (retries > 0) {
      setTimeout(() => this.postMessageToIframe(retries - 1), 1000);
    }
  }

  private onInvalidMember() {
    this.toasterService.error(
      this.translateService.instant('public.checkout.memberOnlyError'),
    );
    this.isMember = false;
    this.logout();
  }

  getAuthQueryParams(): string {
    if (!this.isUserLoggedIn()) return '';
    const authData = this.localStorageService.readSavedState(
      this.tokenCookieName,
    );
    // Story #11691: while a social sign-in is still running, storage holds the
    // provider's payload instead of a session. It has no tokens, so a branch
    // would drop it anyway, and it carries the provider's own token, which
    // must never go into a URL.
    if (!isPersistableAuthData(authData)) return '';
    try {
      const base64 = encodeAuthDataParam(authData);
      return `&${this.authDataParam}=${encodeURIComponent(base64)}`;
    } catch (error) {
      // Every caller opens the branch with whatever this returns, so a session
      // that cannot be encoded opens it signed out rather than not at all.
      console.warn(
        'Could not encode the customer session for the handoff.',
        error,
      );
      return '';
    }
  }

  /**
   * Consume leftover `authData` from the live URL (or the pre-bootstrap
   * snapshot). Invoked from `StoreLoadingComponent` on store LOADED, and the
   * only handoff path this service owns — `main.ts` owns capture/persist/strip.
   *
   * Production path: `main.ts` already captured, persisted and scrubbed the bar
   * before bootstrap, so this is usually a no-op. It still handles
   * search-string links already in the wild, and the snapshot case where a
   * redirect rewrote the fragment before anything could read it. Bar cleanup
   * uses {@link stripAuthDataFromAddressBar} only after a successful persist
   * (or a dud that needs scrubbing) — same rule as pre-bootstrap
   * `persistCapturedAuthDataToStorage` (no Router navigation).
   */
  parseAndSetAuthFromUrl(): void {
    const fromSearch = new URLSearchParams(window.location.search).get(
      this.authDataParam,
    );
    const hashAuth = this.readAuthDataFromHash();
    const landing = peekLandingAuthHandoff();
    // Prefer live hash (child-list handoff), then search (multi-store-checkout /
    // older links), then the pre-bootstrap snapshot if a redirect already
    // rewrote the fragment. Use `||` so an empty `authData=` in the hash does
    // not shadow a real search/landing payload (`??` treats '' as present).
    const authDataParam = hashAuth || fromSearch || landing;

    if (!authDataParam) {
      clearLandingAuthHandoff();
      return;
    }

    if (this.persistAuthDataParam(authDataParam)) {
      stripAuthDataFromAddressBar();
    }
    clearLandingAuthHandoff();
  }

  /** Reads authData from `#/path?authData=…` (HashLocationStrategy). */
  private readAuthDataFromHash(): string | null {
    return readAuthDataFromHashString(window.location.hash);
  }

  /**
   * Decode and persist a handoff payload. Malformed base64/JSON is swallowed so
   * the customer continues anonymously and the dud param can still be stripped.
   * Applies the shared {@link shouldAcceptAuthHandoff} rule so a URL cannot
   * swap an existing customer's session on this path either, not only on the
   * pre-bootstrap one, and stores the payload marked for the check of whose
   * token it is, as that path does, unless it carries the session already
   * stored and checked here ({@link handoffToStore}).
   * @returns true if the bar may be stripped (dud / non-persistable / dropped /
   *          wrote OK). false if a valid payload could not be written.
   */
  private persistAuthDataParam(authDataParam: string): boolean {
    const authData: unknown = parseAuthDataParam(authDataParam);
    if (authData == null) {
      console.warn('Ignoring malformed authData parameter.');
      return true;
    }
    if (!isPersistableAuthData(authData)) {
      return true;
    }
    const payload: AuthHandoffPayload = authData;
    if (!shouldAcceptAuthHandoff(payload)) {
      return true;
    }
    return this.saveState(handoffToStore(payload));
  }
}
